import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";

import type {
  ContactApprovalContactData,
  ContactApprovalReviewResult,
} from "../types";
import type { CrmDataProvider } from "../providers/types";

export type UseM_ContactApproval_Review_Body = {
  decision: "approve" | "reject";
  contact?: ContactApprovalContactData | null;
};

export type UseM_ContactApproval_Review_Params = {
  approvalId: number;
  onChangedState?: () => void;
  onSuccess?: (result: ContactApprovalReviewResult) => void;
};

export const useM_ContactApproval_Review = ({
  approvalId,
  onChangedState,
  onSuccess,
}: UseM_ContactApproval_Review_Params) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["contact_approvals", "review", approvalId],
    mutationFn: (body: UseM_ContactApproval_Review_Body) =>
      dataProvider.reviewContactApproval(
        approvalId,
        body.decision,
        body.contact ?? null,
      ),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contact_approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
      ]);
      notify(`resources.contacts.approvals.notifications.${result.outcome}`, {
        type: "success",
      });
      onSuccess?.(result);
    },
    onError: async (error) => {
      console.error("Contact approval review failed:", error);
      await queryClient.invalidateQueries({
        queryKey: ["contact_approvals"],
      });
      const changedState =
        error instanceof Error && error.message.includes("already");
      notify(
        changedState
          ? "resources.contacts.approvals.notifications.changed"
          : "resources.contacts.approvals.notifications.error",
        { type: "error" },
      );
      if (changedState) onChangedState?.();
    },
  });

  return { mutation };
};
